import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent } from 'react';
import PT from 'prop-types';
import styled, { css } from 'styled-components';
import { $ as $f7dceffc5ad7768b$export$4e328f61c538687f } from '../3rdPartyModules/_react-aria/focus/dist/useFocusRing-BO5gj-B1.js';
import { $ as $3ef42575df84b30b$export$9d1611c77c2fe928 } from '../3rdPartyModules/_react-aria/utils/dist/mergeProps-Bjtexw5f.js';
import { f as focusOutline } from '../FocusIndicator/styleMixins/focusOutline-DNwtedBp.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { T as Text } from '../Text/Text-Cv6a53CC.js';
import { I as Icon } from '../Icon/Icon-DowU4ANk.js';
import { t as times } from '../LocalModules/storm-ui-icons/dist/esm/localIcons-EM4gOTGb-B7bhIt-r.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const StyledPill = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30054544__sc-gv56jb-0"
})(["color:", ";font-size:", ";line-height:", ";width:fit-content;background:", ";padding-inline-start:", ";padding-inline-end:", ";border-radius:", ";cursor:", ";display:flex;align-items:center;", " ", ""], ({
  theme
}) => theme.pillLegacy.color, ({
  theme
}) => theme.pillLegacy.fontSize, ({
  theme
}) => theme.pillLegacy.lineHeight, ({
  theme
}) => theme.pillLegacy.bg, ({
  theme
}) => theme.pillLegacy.paddingInlineStart, ({
  theme
}) => theme.pillLegacy.paddingInlineEnd, ({
  theme
}) => theme.pillLegacy.radius, ({
  disabled
}) => disabled ? 'not-allowed' : 'default', ({
  disabled,
  theme
}) => disabled && `
    color: ${theme.pillLegacy.disabledText};
    background: ${theme.pillLegacy.disabledBg};
    outline: none;
  `, isMobile(css(["font-size:", ";"], ({
  theme
}) => theme.pillLegacy.mobile.fontSize)));
StyledPill.displayName = 'StyledPill';
const StyledPillLabel = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30054544__sc-gv56jb-1"
})(["max-width:", ";overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;"], ({
  fullWidth,
  maxWidth,
  theme
}) => fullWidth ? 'auto' : `${maxWidth}px` || theme.pillLegacy.maxWidth);
StyledPillLabel.displayName = 'StyledPillLabel';
const IconButton = /*#__PURE__*/styled('button').attrs(({
  onFocus,
  onBlur
}) => {
  const {
    isFocusVisible,
    focusProps
  } = $f7dceffc5ad7768b$export$4e328f61c538687f();
  return {
    /*
     *`useFocusRing()` uses `onFocus` and `onBlur` props, so `mergeProps()` must be used to
     * make sure user supplied `onFocus` and `onBlur` are also called.
    */
    ...$3ef42575df84b30b$export$9d1611c77c2fe928({
      onFocus,
      onBlur
    }, focusProps),
    focusVisible: isFocusVisible
  };
}).withConfig({
  componentId: "sc-storm-ui-30054544__sc-gv56jb-2"
})(["color:", ";margin-inline-start:", ";opacity:1;background:none;border:none;border-radius:", ";padding:", ";.svg-inline--fa{vertical-align:middle;}:hover{cursor:", ";color:", ";}:focus{outline:none;", "}", ""], ({
  disabled,
  theme
}) => disabled ? theme.pillLegacy.closeButton.disabledColor : theme.pillLegacy.closeButton.color, ({
  theme
}) => theme.spacing.micro, ({
  theme
}) => theme.pillLegacy.radius, ({
  theme
}) => theme.pillLegacy.iconPadding, ({
  disabled
}) => disabled ? 'not-allowed' : 'pointer', ({
  disabled,
  theme
}) => disabled ? theme.pillLegacy.closeButton.disabledColor : theme.pillLegacy.closeButton.hoverColor, ({
  focusVisible
}) => focusVisible && focusOutline, isMobile(css(["padding:", ";"], ({
  theme
}) => theme.pillLegacy.mobile.iconPadding)));
IconButton.displayName = 'IconButton';
class Pill extends PureComponent {
  constructor(...args) {
    super(...args);
    // Prevent the onClose event from firing the onClick event of the parent.
    _defineProperty(this, "onClose", event => {
      event.stopPropagation();
      if (this.props.onClose) {
        this.props.onClose();
      }
    });
  }
  render() {
    const {
      label,
      disabled,
      fullWidth,
      maxWidth,
      className,
      onClick,
      onClose,
      closeLabel,
      ...rest
    } = this.props;
    return /*#__PURE__*/React__default.createElement(StyledPill, _extends({
      disabled: disabled,
      fullWidth: fullWidth,
      className: className,
      onClick: onClick
    }, rest), /*#__PURE__*/React__default.createElement(StyledPillLabel, {
      disabled: disabled,
      title: label,
      maxWidth: maxWidth
    }, label), /*#__PURE__*/React__default.createElement(IconButton, {
      disabled: disabled,
      onClick: this.onClose,
      type: "button"
    }, /*#__PURE__*/React__default.createElement(Text, {
      type: "sr-only"
    }, closeLabel), /*#__PURE__*/React__default.createElement(Icon, {
      type: times
    })));
  }
}
_defineProperty(Pill, "propTypes", {
  /**
   * The text displayed on the Pill.
   */
  label: PT.string.isRequired,
  /**
   * Used to disable the Pill.
   */
  disabled: PT.bool,
  /**
   * Add text to create a text divider.
   */
  fullWidth: PT.bool,
  /**
   * Configure the max-width css rule. When prop fullWidth is set to true, max-width is auto.
   */
  maxWidth: PT.number,
  /**
   * The class name passed to the styled Pill.
   */
  className: PT.string,
  /**
   * Called when the Pill is clicked.
   */
  onClick: PT.func,
  /**
   * Called when `X` is clicked.
   */
  onClose: PT.func,
  /**
   * Text that a screen reader will read out when the close button is in focus.
   */
  closeLabel: PT.string
});
_defineProperty(Pill, "defaultProps", {
  disabled: false,
  fullWidth: false,
  maxWidth: undefined,
  closeLabel: 'remove',
  className: '',
  onClick: noop,
  onClose: noop
});

export { Pill as P };
//# sourceMappingURL=PillLegacy-DkUcsGix.js.map
